import api from '../../services/api';
import liveChatApi, { centralChatTicketsEnabled } from '../../services/liveChatApi';
import { centralChatPlayerIdentity, sourceChatPlayerIdentity } from '../../services/chatSourceIdentity';

export const CHAT_PLAYER_SEARCH_DEBOUNCE_MS = 250;
export const normalizeChatSearch = (value) => String(value || '').trim().toLowerCase();

// Source profiles are authoritative for new signups, including players who
// have not started a conversation. Chat-room search alone cannot find them.
export async function searchChatPlayerDirectory({ term, tenantId, agentPortal, signal, central = centralChatTicketsEnabled }) {
  const query = String(term || '').trim();
  if (query.length < 2 || !tenantId || ['all', 'none'].includes(tenantId)) return [];
  const sourceTenantId = sourceChatPlayerIdentity('', tenantId).tenantId;
  const chatTenantId = centralChatPlayerIdentity('', tenantId, central).tenantId;
  let players;
  if (!agentPortal) {
    const params = new URLSearchParams({
      // The users endpoint accepts a regex; type-ahead must match literal text.
      search: query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
      tenant_id: sourceTenantId,
      players_only: 'true',
      limit: '50',
    });
    try {
      const response = await api.get(`/api/admin/users?${params}`, { signal });
      players = response.data?.users || [];
    } catch (error) {
      // Chat-only staff need not gain users:view. Agents never use this API.
      if (error.response?.status !== 403) throw error;
    }
  }
  if (!players) {
    const params = new URLSearchParams({ q: query, tenant_id: chatTenantId, limit: '50' });
    const response = await liveChatApi.get(`/api/chat/staff/players/search?${params}`, { signal });
    players = response.data?.players || [];
  }
  const seen = new Set();
  return players.map((player) => {
    const identity = centralChatPlayerIdentity(player.id, player.tenant_id, central);
    return { ...player, id: identity.playerId, tenant_id: identity.tenantId };
  }).filter((player) => {
    if (!player?.id || player.tenant_id !== chatTenantId || seen.has(player.id)) return false;
    seen.add(player.id);
    return true;
  });
}

export function unmatchedChatPlayers(players, rooms) {
  const existing = new Set(rooms.map((room) => room.player_id));
  return players.filter((player) => !existing.has(player.id));
}

export async function openDirectoryPlayerChat(player, tenantId, central = centralChatTicketsEnabled) {
  const identity = centralChatPlayerIdentity(player?.id, player?.tenant_id, central);
  const chatTenantId = centralChatPlayerIdentity('', tenantId, central).tenantId;
  if (!player?.id || !tenantId || identity.tenantId !== chatTenantId) {
    throw new Error('Player is outside the current chat workspace');
  }
  // Searching never creates rooms or messages. This explicit click reuses the
  // existing central conversation and keeps server tenant/agent checks intact.
  const response = await liveChatApi.post('/api/chat/staff/conversations/direct', {
    player_id: identity.playerId,
    tenant_id: chatTenantId,
  });
  const room = response.data?.conversation;
  if (!room?.id || room.player_id !== identity.playerId || room.tenant_id !== chatTenantId) {
    throw new Error('Could not open the player in this chat workspace');
  }
  return room;
}
